import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

export interface PromptTemplate {
  id: string;
  name: string;
  template_type: 'resume' | 'assessment' | 'jobParsing';
  description?: string;
  template_content: string;
  variables?: string[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

// GET - List all prompt templates
export const usePromptTemplates = () => {
  return useQuery({
    queryKey: ['prompt-templates'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/prompt-templates`, { headers });
      if (!response.ok) throw new Error('Failed to fetch prompt templates');
      const templates = await response.json() as PromptTemplate[];
      // Parse variables from JSON string if needed
      return templates.map((t) => ({
        ...t,
        variables: typeof t.variables === 'string' ? JSON.parse(t.variables || '[]') : t.variables,
      }));
    },
  });
};

// GET - List templates by type
export const usePromptTemplatesByType = (templateType: 'resume' | 'assessment' | 'jobParsing') => {
  return useQuery({
    queryKey: ['prompt-templates', templateType],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/prompt-templates/type/${templateType}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch prompt templates');
      const templates = await response.json() as PromptTemplate[];
      // Parse variables from JSON string if needed
      return templates.map((t) => ({
        ...t,
        variables: typeof t.variables === 'string' ? JSON.parse(t.variables || '[]') : t.variables,
      }));
    },
  });
};

// GET - Get single template by ID
export const usePromptTemplate = (id: string) => {
  return useQuery({
    queryKey: ['prompt-templates', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/prompt-templates/${id}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch prompt template');
      const template = await response.json() as PromptTemplate;
      // Parse variables from JSON string if needed
      return {
        ...template,
        variables: typeof template.variables === 'string' ? JSON.parse(template.variables || '[]') : template.variables,
      };
    },
    enabled: !!id,
  });
};

// POST - Create new template
export const useCreatePromptTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Omit<PromptTemplate, 'id' | 'created_at' | 'updated_at'>) => {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/prompt-templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create prompt template');
      return response.json() as Promise<PromptTemplate>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt-templates'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-templates', 'resume'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-templates', 'assessment'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-templates', 'jobParsing'] });
    },
  });
};

// PUT - Update template
export const useUpdatePromptTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PromptTemplate> }) => {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/prompt-templates/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update prompt template');
      return response.json() as Promise<PromptTemplate>;
    },
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: ['prompt-templates'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-templates', id] });
      if (data.template_type) {
        queryClient.invalidateQueries({ queryKey: ['prompt-templates', data.template_type] });
      }
    },
  });
};

// DELETE - Delete template
export const useDeletePromptTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/prompt-templates/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) throw new Error('Failed to delete prompt template');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt-templates'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-templates', 'resume'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-templates', 'assessment'] });
      queryClient.invalidateQueries({ queryKey: ['prompt-templates', 'jobParsing'] });
    },
  });
};

